import React, { useState, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { uploadFile } from "../../firebase/fileUpload";
import {
  loadStateOptions,
  loadCityOptions,
  loadDistrictOptions,
  loadPincodeOptions,
} from "../utilities/locationApi";
import collapseIcon from "../event/assets/reduce.png";
import expandIcon from "../event/assets/expand-arrows.png";
import { FaTrash, FaEdit } from "react-icons/fa";

const SponsorDetailForm = ({
  savedCards = [],
  onSponsorDataChange,
  onDeleteSponsor,
  onUpdateSponsor,
  readOnly = false,
  sponsorTermsConditions = "",
  onTermsConditionsChange,
}) => {
  const initialSponsorData = {
    sponsorCategory: "",
    noOfCoupons: "999999",
    noOfGiftHampers: "",
    description: "",
    price: "",
    additional_data: {
      country: "India",
      state: [],
      city: [],
      district: [],
      pincode: [],
      is_national: true,
      is_state: false,
      is_city: false,
      is_district: false,
      is_pincode: false,
      is_international: false,
      location: "National",
      no_of_seats: "N/A",
      proposal_url: "N/A",
    },
  };

  const [sponsorData, setSponsorData] = useState(initialSponsorData);
  const [editingIndex, setEditingIndex] = useState(-1);

  const [selectedCategories, setSelectedCategories] = useState({
    state: [],
    city: [],
    district: [],
    pincode: [],
  });
  const [isSponsorOpen, setIsSponsorOpen] = useState(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSponsorData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle Additional Data Changes
  const handleAdditionalDataChange = (field, value) => {
    setSponsorData((prev) => ({
      ...prev,
      additional_data: {
        ...prev.additional_data,
        [field]: value,
      },
    }));
  };

  const clearAll = useCallback(() => {
    setSelectedCategories({ state: [], city: [], district: [], pincode: [] });
    setSponsorData((prev) => ({
      ...prev,
      additional_data: {
        ...prev.additional_data,
        state: [],
        city: [],
        district: [],
        pincode: [],
      },
    }));
  }, []);

  const handleCheckboxChange = (level) => {
    const ad = sponsorData.additional_data;
    const toggled = !ad[level];

    if (level === "is_international") {
      setSponsorData((prev) => ({
        ...prev,
        additional_data: {
          ...prev.additional_data,
          is_international: toggled,
          is_national: false,
          is_state: false,
          is_city: false,
          is_district: false,
          is_pincode: false,
          location: toggled ? "International" : "",
        },
      }));
      clearAll();
    } else if (level === "is_national") {
      setSponsorData((prev) => ({
        ...prev,
        additional_data: {
          ...prev.additional_data,
          is_national: toggled,
          is_state: false,
          is_city: false,
          is_district: false,
          is_pincode: false,
          is_international: false,
          location: toggled ? "National" : "",
        },
      }));
      clearAll();
    } else if (level === "is_district") {
      setSponsorData((prev) => ({
        ...prev,
        additional_data: {
          ...prev.additional_data,
          is_district: toggled,
          is_national: false,
          is_state: false,
          is_city: false,
          is_pincode: false,
          is_international: false,
          location: toggled ? "District" : "",
        },
      }));
      clearAll();
    } else {
      // is_state | is_city | is_pincode
      setSponsorData((prev) => ({
        ...prev,
        additional_data: {
          ...prev.additional_data,
          is_international: false,
          is_national: false,
          is_district: false,
          is_state: level === "is_state" ? toggled : false,
          is_city: level === "is_city" ? toggled : false,
          is_pincode: level === "is_pincode" ? toggled : false,
          location: "",
        },
      }));
      clearAll();
    }
  };

  // Generic onChange handler for all AsyncSelect location fields.
  const handleMultiSelectChange = useCallback(
    (selectedOptions, categoryType) => {
      const opts = selectedOptions || [];
      const values = opts.map((o) => o.value);
      setSelectedCategories((prev) => ({ ...prev, [categoryType]: opts }));
      handleAdditionalDataChange(categoryType, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const url = await uploadFile(file);
        handleAdditionalDataChange("proposal_url", url);
      } catch (error) {
        console.error("File upload failed:", error);
      }
    }
  };

  const handleSaveData = () => {
    if (
      !sponsorData.sponsorCategory ||
      !sponsorData.description ||
      !sponsorData.price
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    // Preserve existing sponsorCategoryId so the backend UPDATE path can
    // auto-generate one if it was empty, or keep the existing valid one.
    const dataToSave = {
      ...sponsorData,
    };

    if (editingIndex !== -1) {
      // Replacing an existing card — let parent update the array in place
      if (onUpdateSponsor) onUpdateSponsor(editingIndex, dataToSave);
      setEditingIndex(-1);
    } else {
      onSponsorDataChange(dataToSave);
    }

    // Reset the form
    setSponsorData({
      sponsorCategory: "",
      noOfCoupons: "999999",
      noOfGiftHampers: "",
      description: "",
      price: "",
      additional_data: {
        country: "India",
        state: [],
        city: [],
        district: [],
        pincode: [],
        is_national: true,
        is_state: false,
        is_city: false,
        is_district: false,
        is_pincode: false,
        is_international: false,
        location: "National",
        no_of_seats: "N/A",
        proposal_url: "N/A",
      },
    });
    setSelectedCategories({ state: [], city: [], district: [], pincode: [] });
    setEditingIndex(-1);
  };

  const handleDeleteCard = (index) => {
    if (onDeleteSponsor) {
      onDeleteSponsor(index);
    }
  };

  const toSelectOptions = (arr) =>
    Array.isArray(arr) ? arr.map((v) => ({ label: v, value: v })) : [];

  const handleEditCard = (index) => {
    const card = savedCards[index];
    if (!card) return;
    const ad = card.additional_data || {};
    setSponsorData({
      sponsorCategoryId: card.sponsorCategoryId || "",
      sponsorCategory: card.sponsorCategory || "",
      noOfCoupons: card.noOfCoupons || "999999",
      noOfGiftHampers: card.noOfGiftHampers || "",
      description: card.description || "",
      price: card.price || "",
      additional_data: {
        country: ad.country || "India",
        state: ad.state || [],
        city: ad.city || [],
        district: ad.district || [],
        pincode: ad.pincode || [],
        is_national: !!ad.is_national,
        is_state: !!ad.is_state,
        is_city: !!ad.is_city,
        is_district: !!ad.is_district,
        is_pincode: !!ad.is_pincode,
        is_international: !!ad.is_international,
        location: ad.location || "",
        no_of_seats: ad.no_of_seats || "N/A",
        proposal_url: ad.proposal_url || "N/A",
      },
    });
    setSelectedCategories({
      state: toSelectOptions(ad.state),
      city: toSelectOptions(ad.city),
      district: toSelectOptions(ad.district),
      pincode: toSelectOptions(ad.pincode),
    });
    setEditingIndex(index);
    setIsSponsorOpen(true);
    // Scroll the form into view
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTextareaResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  return (
    <div className="bg-blue-50 p-4 rounded-lg shadow-lg border my-4">
      {!readOnly && (
        <div className="w-full flex items-center justify-between">
          <h3 className="text-xl font-semibold text-blue-600 rounded-md mb-5">
            Sponsor Details
          </h3>
          <button
            type="button"
            onClick={() => setIsSponsorOpen(!isSponsorOpen)}
            className="flex items-center"
          >
            <img
              src={isSponsorOpen ? collapseIcon : expandIcon}
              alt={isSponsorOpen ? "Collapse" : "Expand"}
              className="w-6 h-6"
            />
          </button>
        </div>
      )}
      {/* ── Global Sponsor Terms & Conditions ── */}
      {!readOnly && (
        <div className="mb-6">
          <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
            Sponsor Terms &amp; Conditions <span className="text-gray-400 text-sm">(applies to all packages)</span>
          </label>
          <textarea
            value={sponsorTermsConditions}
            onChange={(e) => onTermsConditionsChange && onTermsConditionsChange(e.target.value)}
            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px`; }}
            className="w-full min-h-[120px] border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="Enter terms and conditions that apply to all sponsor packages"
          />
        </div>
      )}
      {readOnly && sponsorTermsConditions && (
        <div className="bg-white p-4 mb-4 rounded-lg shadow-md">
          <p className="whitespace-pre-line">
            <strong>Sponsor Terms &amp; Conditions:</strong> {sponsorTermsConditions}
          </p>
        </div>
      )}

      {isSponsorOpen && !readOnly && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="relative">
              <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
                Sponsor Category <span className="text-red-600"> *</span>
              </label>
              <input
                type="text"
                name="sponsorCategory"
                value={sponsorData.sponsorCategory}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter sponsor category"
              />
            </div>
            <div className="relative">
              <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
                No of Coupons (Default: 999999)
              </label>
              <input
                type="number"
                name="noOfCoupons"
                value={sponsorData.noOfCoupons}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter number of coupons"
              />
            </div>
            <div className="relative">
              <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
                No of Gift Hampers <span className="text-red-600"> *</span>
              </label>
              <input
                type="number"
                name="noOfGiftHampers"
                value={sponsorData.noOfGiftHampers}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter number of gift hampers"
              />
            </div>
            <div className="relative">
              <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
                Price <span className="text-red-600"> *</span>
              </label>
              <input
                type="number"
                name="price"
                value={sponsorData.price}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter price"
              />
            </div>
          </div>

          <div className="my-8 lg:w-full relative">
            <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
              Description <span className="text-red-600"> *</span>
            </label>
            <textarea
              name="description"
              value={sponsorData.description}
              onChange={handleInputChange}
              onInput={handleTextareaResize}
              className="w-full min-h-[150px] border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Enter description"
            />
          </div>

          <h3 className="text-lg font-medium text-gray-700 mb-3">
            Sponsor Location Detail
          </h3>
          <div className="mb-4 flex flex-wrap gap-8">
            {[
              { key: "is_international", label: "International" },
              { key: "is_national",      label: "National" },
              { key: "is_state",         label: "State" },
              { key: "is_district",      label: "District" },
              { key: "is_city",          label: "City" },
              { key: "is_pincode",       label: "Pincode" },
            ].map(({ key, label }) => (
              <label key={key} className="mr-4 flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!sponsorData.additional_data[key]}
                  onChange={() => handleCheckboxChange(key)}
                  className="h-4 w-4"
                />
                <span className="ml-2">{label}</span>
              </label>
            ))}
          </div>

          {/* ── Live-search dropdowns via backend location API ─────────────── */}

          {sponsorData.additional_data.is_state && (
            <div className="mt-6 mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search & select state(s)</label>
              <AsyncSelect
                isMulti
                cacheOptions
                loadOptions={loadStateOptions}
                value={selectedCategories.state}
                onChange={(opts) => handleMultiSelectChange(opts, "state")}
                placeholder="Type to search states…"
                noOptionsMessage={({ inputValue }) =>
                  inputValue.length < 2 ? "Type at least 2 characters" : "No results"
                }
                className="w-full"
              />
            </div>
          )}

          {sponsorData.additional_data.is_district && (
            <div className="mt-6 mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search & select district(s)</label>
              <AsyncSelect
                isMulti
                cacheOptions
                loadOptions={loadDistrictOptions}
                value={selectedCategories.district}
                onChange={(opts) => handleMultiSelectChange(opts, "district")}
                placeholder="Type to search districts…"
                noOptionsMessage={({ inputValue }) =>
                  inputValue.length < 2 ? "Type at least 2 characters" : "No results"
                }
                className="w-full"
              />
            </div>
          )}

          {sponsorData.additional_data.is_city && (
            <div className="mt-6 mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search & select city / cities</label>
              <AsyncSelect
                isMulti
                cacheOptions
                loadOptions={loadCityOptions}
                value={selectedCategories.city}
                onChange={(opts) => handleMultiSelectChange(opts, "city")}
                placeholder="Type to search cities…"
                noOptionsMessage={({ inputValue }) =>
                  inputValue.length < 2 ? "Type at least 2 characters" : "No results"
                }
                className="w-full"
              />
            </div>
          )}

          {sponsorData.additional_data.is_pincode && (
            <div className="mt-6 mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search & select pincode(s)</label>
              <AsyncSelect
                isMulti
                cacheOptions
                loadOptions={loadPincodeOptions}
                value={selectedCategories.pincode}
                onChange={(opts) => handleMultiSelectChange(opts, "pincode")}
                placeholder="Type a pincode or area name…"
                noOptionsMessage={({ inputValue }) =>
                  inputValue.length < 2 ? "Type at least 2 characters" : "No results"
                }
                className="w-full"
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="my-4">
              <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
                Sponsor Entry (No of Seats){" "}
                <span className="text-red-600"> *</span>
              </label>
              <input
                type="number"
                value={sponsorData.additional_data.no_of_seats}
                onChange={(e) =>
                  handleAdditionalDataChange("no_of_seats", e.target.value)
                }
                className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter number of seats"
              />
            </div>
            <div className="my-4">
              <label className="block text-sm lg:text-lg font-medium text-gray-700 mb-2">
                Proposal Document
              </label>
              <input
                type="file"
                onChange={handleFileUpload}
                className="w-full border bg-white border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none file:mr-4 file:py- file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleSaveData}
            className="bg-blue-500 text-white px-4 py-2 rounded-md mt-4"
          >
            {editingIndex !== -1 ? "Update Sponsor Data" : "Save Sponsor Data"}
          </button>
        </>
      )}

      {savedCards &&
        savedCards.map((card, index) => (
          <div
            key={index}
            className="bg-white p-4 mt-6 rounded-lg shadow-md relative"
          >
            {!readOnly && (
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => handleEditCard(index)}
                  title="Edit this sponsor package"
                  className="text-blue-500 hover:text-blue-700"
                >
                  <FaEdit />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteCard(index)}
                  title="Delete this sponsor package"
                  className="text-red-500 hover:text-red-700"
                >
                  <FaTrash />
                </button>
              </div>
            )}
            <h3 className="text-lg font-semibold mb-4">
              Saved Sponsor Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p>
                <strong>Category:</strong> {card.sponsorCategory || "N/A"}
              </p>
              <p>
                <strong>No of Coupons:</strong> {card.noOfCoupons || "N/A"}
              </p>
              <p>
                <strong>No of Gift Hampers:</strong>{" "}
                {card.noOfGiftHampers || "N/A"}
              </p>
              <p>
                <strong>Price:</strong> {card.price || "N/A"}
              </p>
              <p className="md:col-span-2 whitespace-pre-line">
                <strong>Description:</strong> {card.description || "N/A"}
              </p>
              <p>
                <strong>Location:</strong>{" "}
                {card.additional_data.location || "N/A"}
              </p>
              <p>
                <strong>States:</strong>{" "}
                {Array.isArray(card.additional_data.state) &&
                card.additional_data.state.length > 0
                  ? card.additional_data.state.join(", ")
                  : "N/A"}
              </p>
              <p>
                <strong>Districts:</strong>{" "}
                {Array.isArray(card.additional_data.district) &&
                card.additional_data.district.length > 0
                  ? card.additional_data.district.join(", ")
                  : "N/A"}
              </p>
              <p>
                <strong>Cities:</strong>{" "}
                {Array.isArray(card.additional_data.city) &&
                card.additional_data.city.length > 0
                  ? card.additional_data.city.join(", ")
                  : "N/A"}
              </p>
              <p>
                <strong>Pincodes:</strong>{" "}
                {Array.isArray(card.additional_data.pincode) &&
                card.additional_data.pincode.length > 0
                  ? card.additional_data.pincode.join(", ")
                  : "N/A"}
              </p>
              <p>
                <strong>No of Seats:</strong>{" "}
                {card.additional_data.no_of_seats || "N/A"}
              </p>
              <p>
                <strong>Proposal URL:</strong>{" "}
                {card.additional_data.proposal_url !== "N/A" ? (
                  <a
                    href={card.additional_data.proposal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    View Document
                  </a>
                ) : (
                  "N/A"
                )}
              </p>
            </div>
          </div>
        ))}
    </div>
  );
};

export default SponsorDetailForm;
